import React, { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { Text } from '@atlaskit/primitives/compiled';

import type { UpdateWorkItemAllFieldTypesAction } from '../../../../../common/types';
import { FieldTooltipContent } from '../../../../../common/ui/FieldTooltipContent';
import {
	type RegisterTooltipProvider,
	useRegisterTooltipContentProvider,
} from '../../../../../common/utils/useRegisterTooltipContentProvider';
import { getActionData } from '../../../utils';

import type { singleLineTextField_updateWorkItemAllFieldTypesActionRenderer$key } from './__generated__/singleLineTextField_updateWorkItemAllFieldTypesActionRenderer.graphql';

const messages = defineMessages({
	updateAction: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.single-line-text-field.update-action',
		defaultMessage: 'Update {fieldName}',
		description: 'Label for the change action in the single line text field',
	},
	clearAction: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.single-line-text-field.clear-action',
		defaultMessage: 'Clear {fieldName}',
		description: 'Label for the clear action in the single line text field',
	},
});

export type Props = {
	queryRef: singleLineTextField_updateWorkItemAllFieldTypesActionRenderer$key;
	action: UpdateWorkItemAllFieldTypesAction;
	registerTooltipProvider?: RegisterTooltipProvider;
};

export const SingleLineTextField = ({
	queryRef,
	action,
	registerTooltipProvider,
}: Props): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	const { fieldKey, fieldValue, fieldReasoning } = getActionData(action);

	const data = useFragment(
		graphql`
			fragment singleLineTextField_updateWorkItemAllFieldTypesActionRenderer on JiraSingleLineTextField {
				fieldId
				name
				text
			}
		`,
		queryRef,
	);

	const isFieldRenderable = data?.fieldId === fieldKey;

	const isUpdatingAction = Boolean(fieldValue);

	const toolTipContentProvider = useCallback(
		() => (
			<FieldTooltipContent
				reasoning={fieldReasoning}
				fieldName={data?.name}
				current={data?.text}
				suggested={fieldValue ? String(fieldValue) : undefined}
			/>
		),
		[fieldValue, fieldReasoning, data?.name, data?.text],
	);

	useRegisterTooltipContentProvider({
		registerTooltipProvider,
		fieldId: fieldKey,
		provider: toolTipContentProvider,
		enabled: isFieldRenderable,
	});

	if (!data?.fieldId || !data?.name || data?.fieldId !== fieldKey) {
		return null;
	}

	return (
		<Text
			color="color.text.subtle"
			maxLines={1}
			testId="update-work-item-all-fields-action-renderer.ui.action-card.card-fields-list.single-line-text-field"
		>
			{formatMessage(isUpdatingAction ? messages.updateAction : messages.clearAction, {
				fieldName: data?.name?.toLowerCase(),
			})}
		</Text>
	);
};
